import {
  AuthFormGridStyled,
  AuthFormPanelStyled,
  AuthShellStyled,
} from '@OnArk/styles/Auth.styled';
import { SubmitButtonStyled } from '@OnArk/styles/Buttons.styled';
import {
  ErrorMsgsStyled,
  InputStyled,
  LabelStyled,
} from '@OnArk/styles/Form.styled';

import React, { useState } from 'react';

import Link from 'next/link';

import Form from '@hangar31/rc-form';
import AuthBrandPanel, { DEFAULT_AUTH_FEATURES } from '@OnArk/AuthBrandPanel';
import { AuthEmailField } from '@OnArk/AuthFields';
import { internalPathOr } from '@onark/shared/internalPath';
import { secureCookie } from '@OnArk/utils/cookieSecure';
import posthog from '@OnArk/utils/posthog';
import { requestSignInLink } from '@OnArk/utils/requestSignInLink';
import { withAuth, withClient } from '@OnArk/withGraph';
import Cookies from 'universal-cookie';

function Login({ client, to }: LoginPageProps) {
  // Passwordless is the default path: magic link works for every account
  // (password users included), so we lead with it and keep password sign-in
  // as an opt-in fallback toggle.
  const [magicMode, setMagicMode] = useState(true);
  const [linkSent, setLinkSent] = useState(false);

  const cookies = new Cookies();

  return (
    <AuthShellStyled>
      <AuthBrandPanel
        features={DEFAULT_AUTH_FEATURES}
        headline="Welcome back to your church classes."
        lede="Manage your courses, check on your guests, and keep your church connected — all in one place."
      />
      <AuthFormPanelStyled>
        <div>
          <h2>Log in</h2>
          <p>
            {magicMode
              ? 'Enter your email and we’ll send you a link to sign in — no password needed.'
              : 'Welcome back. Enter your credentials to access your admin.'}
          </p>
          {magicMode && linkSent ? (
            <p css={{ marginTop: '2rem' }}>
              <strong>Check your email.</strong> If an account exists, a sign-in
              link is on its way. The link expires in 30 minutes.
            </p>
          ) : magicMode ? (
            <Form
              defaultData={{ email: '' }}
              onSubmit={async info => {
                await requestSignInLink(client, info.email, to);
                posthog?.capture('requestedSignInLink');
              }}
              onSuccess={() => setLinkSent(true)}
            >
              <AuthFormGridStyled grid>
                <AuthEmailField required />
                <ErrorMsgsStyled />
                <SubmitButtonStyled>Email me a sign-in link</SubmitButtonStyled>
              </AuthFormGridStyled>
            </Form>
          ) : (
            <Form
              defaultData={{ email: '', password: '' }}
              onSubmit={async info => {
                try {
                  const { data } = await client.login(info);

                  cookies.set('onark-token', data?.token, {
                    path: '/',
                    secure: secureCookie(),
                  });
                  posthog?.identify(info.email, { email: info.email });
                  posthog?.capture('userLoggedIn', { method: 'password' });
                  // Guards against an open redirect via ?to=https://… or //host.
                  window.location.href = internalPathOr(to, '/admin/classes');
                } catch (e) {
                  // Only a credential rejection (the API answered with a
                  // GraphQL error) is "Invalid Login". A dropped request or a
                  // 5xx is rethrown as itself, so an outage is not reported
                  // as a wrong password.
                  const answered = (e as { response?: { errors?: unknown[] } })
                    ?.response?.errors?.length;

                  if (answered) {
                    throw Object.assign(new Error('Invalid Login'), {
                      expected: true,
                    });
                  }

                  throw e;
                }
              }}
            >
              <AuthFormGridStyled grid>
                <AuthEmailField />
                <LabelStyled>
                  <span>Password</span>
                  <InputStyled
                    name="password"
                    placeholder="••••••••"
                    type="password"
                  />
                  <Link className="forgot-link" href="/forgot">
                    Forgot password?
                  </Link>
                </LabelStyled>
                <ErrorMsgsStyled />
                <SubmitButtonStyled>Log in</SubmitButtonStyled>
              </AuthFormGridStyled>
            </Form>
          )}
          {!linkSent && (
            <p className="auth-switch">
              <button
                css={theme => ({
                  background: 'none',
                  border: 'none',
                  color: theme.colors.primary,
                  cursor: 'pointer',
                  font: 'inherit',
                  padding: 0,
                })}
                type="button"
                onClick={() => setMagicMode(m => !m)}
              >
                {magicMode
                  ? 'Use a password instead'
                  : '← Email me a sign-in link instead'}
              </button>
            </p>
          )}
          <p className="auth-switch">
            New here?&nbsp;<Link href="/sign-up">Create an account</Link>
          </p>
        </div>
      </AuthFormPanelStyled>
    </AuthShellStyled>
  );
}
export type LoginPageProps = ServerSidePagePropsWithClient<
  typeof getServerSideProps
>;
export const getServerSideProps = withAuth(({ query }) => {
  return {
    props: {
      layout: 'PUBLIC',
      pageDescription:
        'Log into OnArk here using your administrator credentials.',
      pageTitle: 'Login | OnArk',
      to: (query.to as string) || '',
    },
  };
}, 'REDIRECT');

export default withClient(Login);
